import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import ByzMuiDataGrid from 'src/components/core-components/mui/data-grid';
import { ByzMuiCheckBox } from 'src/components/core-components/mui/ByzMuiCheckBox';
import { useTranslate } from 'src/locales';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
//MUZUN;448072;14042025
export const useData = (ptId?: number) => {
  const { t } = useTranslate();

  const { data: treeHistory, isLoading, isFetching } = useBaseQuery({
    endpoint: `nurse/api/PatientNurseCarePlan/GetPatientNurseCarePlanWithoutDate/${ptId}`,
    queryKey: ["history", ptId],
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const apiData = ((treeHistory as { data?: any[] })?.data ?? []) as any[];

  const thirdGridColumns: GridColDef[] = [
    {
      field: 'check',
      headerName: '',
      width: 62,
      renderCell: () => <ByzMuiCheckBox />
    },
    { field: 'id', headerName: 'No', width: 50 },
    { field: 'name', headerName: t("patient-care-history.nursing-intervention"), width: 200 },
    {
      field: 'period',
      headerName: t("patient-care-history.period"),
      width: 120,
      renderCell: (params) => {
        const val = parseFloat(params.value);
        return (params.value && !isNaN(val)) ? (val / 60) + " " + t("hour") : "";
      }
    },
    {
      field: 'status1',
      headerName: t("patient-care-history.reached"),
      width: 110,
      renderCell: (params) => params.value === "1" ? t("patient-care-history.reached") : "",
      cellClassName: (params) => {
        if (params.value === "1") {
          return 'status-green-cell';
        }
        return '';
      },
    },
    {
      field: 'status2',
      headerName: t("patient-care-history.not-reached"),
      width: 130,
      renderCell: (params) => params.value === "1" ? t("patient-care-history.not-reached") : "",
      cellClassName: (params) => {
        if (params.value === "1") {
          return 'status-red-cell';
        }
        return '';
      },
    },
    { field: 'processTime', headerName: t("patient-care-history.procedure-time"), width: 160 },
    { field: 'processor', headerName: t("patient-care-history.performed-by"), width: 120 },
    { field: 'note', headerName: t("patient-care-history.note"), width: 120 }
  ];

  const secondGridColumns: GridColDef[] = [
    {
      field: 'check',
      headerName: '',
      width: 62,
      renderCell: () => <ByzMuiCheckBox />
    },
    { field: 'title', headerName: '', flex: 1 },
    {
      field: 'is_reached',
      headerName: t("patient-care-history.reached"),
      width: 110,
      renderCell: (params) => params.value === "1" ? t("patient-care-history.reached") : "-",
      cellClassName: (params) => {
        if (params.value === "1") {
          return 'status-green-cell';
        }
        return '';
      },
    },
    {
      field: 'is_not_reached',
      headerName: t("patient-care-history.not-reached"),
      width: 130,
      renderCell: (params) => params.value === "1" ? t("patient-care-history.not-reached") : "-",
      cellClassName: (params) => {
        if (params.value === "1") {
          return 'status-red-cell';
        }
        return '';
      },
    },
    { field: 'processTime', headerName: t("patient-care-history.procedure-time"), width: 130 },
    { field: 'note', headerName: t("patient-care-history.note"), width: 120 }
  ];

  // outerGridRows declaration moved below with useMemo

  const outerGridColumns: GridColDef[] = [
    {
      field: 'check',
      headerName: '',
      width: 50,
      renderCell: () => <ByzMuiCheckBox />
    },
    { field: 'title', headerName: '', flex: 1 }
  ];

  const renderThirdGrid = (params: GridRenderCellParams) => {
    const rows = (params.row as any).childData || [];
    return (
      <Box sx={{ p: 1 }}>
        <ByzMuiDataGrid
          rows={rows}
          columns={thirdGridColumns}
          pageSize={rows.length < 5 ? 5 : 10}
          columnHeaderHeight={40}
          sx={{ "& .MuiDataGrid-columnHeaders": { display: "flex" } }}
        />
      </Box>
    );
  };

  const renderSecondGrid = (params: GridRenderCellParams) => {
    const rows = (params.row as any).childData || [];
    return (
      <Box sx={{ p: 1 }}>
        <ByzMuiDataGrid
          rows={rows}
          columns={secondGridColumns}
          pageSize={5}
          getDetailPanelContent={renderThirdGrid}
          getDetailPanelHeight={() => 220}
          columnHeaderHeight={0}
          sx={{ "& .MuiDataGrid-columnHeaders": { display: "none" } }}
        />
      </Box>
    );
  };
  // API'den gelen veriyi grid şemasına uygun şekilde gruplandıran fonksiyon

  const outerGridRows = useMemo(() => {
    const groupMap = new Map<string, any[]>();
    apiData.forEach(plan => {
      let groupTitle = "";
      if (plan.ctime) {
        const date = new Date(plan.ctime);
        groupTitle = date.toLocaleDateString('tr-TR');
      } else if (plan.plan_time) {
        groupTitle = plan.plan_time.split(' ')[0];
      } else {
        groupTitle = "-";
      }

      if (!groupMap.has(groupTitle)) {
        groupMap.set(groupTitle, []);
      }
      groupMap.get(groupTitle)!.push(plan);
    });

    let globalId = 1;
    const sortedEntries = Array.from(groupMap.entries()).sort(([dateStrA], [dateStrB]) => {
      const parseDate = (dateStr: string): Date => {
        if (dateStr === "-") return new Date(0);
        const parts = dateStr.split('.');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        return new Date(dateStr);
      };
      const dateA = parseDate(dateStrA);
      const dateB = parseDate(dateStrB);
      return dateB.getTime() - dateA.getTime();
    });

    return sortedEntries.map(([dateStr, plans]) => ({
      id: globalId++,
      title: dateStr,
      checked: false,
      childData: plans.map((plan: any) => ({
        id: globalId++,
        ncp_id: plan.ncp_id,
        checked: false,
        title: plan.ncp_name,
        careTime: plan.plan_time,
        plan: plan.ncp_name,
        target: plan.target,
        is_reached: plan.is_reached,
        is_not_reached: plan.is_not_reached,
        processTime: plan.process_time,
        note: plan.note,
        childData: (plan.patientnursecareplandetails ?? []).map((detail: any) => ({
          id: globalId++,
          ncpd_id: detail.ncpd_id,
          checked: false,
          name: detail.ncpd_name,
          period: detail.period,
          is_reached: detail.is_reached,
          is_not_reached: detail.is_not_reached,
          processTime: detail.process_time,
          processor: detail.cuser,
          note: detail.note
        }))
      }))
    }));
  }, [apiData]);

  return {
    t,
    thirdGridColumns,
    secondGridColumns,
    outerGridRows,
    outerGridColumns,
    renderThirdGrid,
    renderSecondGrid,
    isLoading,
    isFetching
  };
};